import React, { useState, useEffect } from 'react';
import { Character } from '../types/character';
import { universeLabels, Universe } from '../types/template';
import { X, Search, Loader2, RefreshCw, Filter, ChevronDown } from 'lucide-react';
import { apiGet } from '../lib/api';
import CharacterPreview from './CharacterPreview';

interface Props {
  onClose: () => void;
}

export default function SearchModal({ onClose }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUniverse, setSelectedUniverse] = useState<Universe | 'all'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'universe'>('recent');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [results, setResults] = useState<Character[]>([]);
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const searchCharacters = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ q: searchTerm, sort: sortBy });
      if (selectedUniverse !== 'all') params.set('universe', selectedUniverse);
      const characters = await apiGet<Character[]>(`/api/characters/published/search?${params}`, false);
      setResults(characters);
    } catch (err) {
      console.error('Erreur lors de la recherche:', err);
      setError('Une erreur est survenue lors de la recherche. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshResults = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try { await searchCharacters(); } finally { setIsRefreshing(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => searchCharacters(), 300);
    return () => clearTimeout(t);
  }, [searchTerm, selectedUniverse, sortBy]);

  if (selectedCharacter) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
        <div className="min-h-screen">
          <div className="max-w-6xl mx-auto">
            <CharacterPreview
              character={selectedCharacter}
              onClose={() => setSelectedCharacter(null)}
              readOnly
              background="bg-transparent"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8">
        <div className="sticky top-0 bg-white rounded-t-lg z-10">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-xl font-semibold">Rechercher des personnages</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 border-b">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par nom, description, univers..."
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 rounded-lg flex items-center gap-1 transition-colors
                  ${showFilters ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filtres</span>
                <ChevronDown className={`w-4 h-4 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              <button
                onClick={refreshResults}
                disabled={isRefreshing}
                className={`px-3 py-2 rounded-lg flex items-center gap-1 transition-colors ${
                  isRefreshing ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Actualiser</span>
              </button>
            </div>

            {showFilters && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
                <div className="flex flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Univers</label>
                    <select
                      value={selectedUniverse}
                      onChange={(e) => setSelectedUniverse(e.target.value as Universe | 'all')}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">Tous les univers</option>
                      {Object.entries(universeLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trier par</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'recent' | 'name' | 'universe')}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="recent">Plus récents</option>
                      <option value="name">Nom</option>
                      <option value="universe">Univers</option>
                    </select>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-600">
                    {results.length} personnage{results.length !== 1 ? 's' : ''} trouvé{results.length !== 1 ? 's' : ''}
                    {selectedUniverse !== 'all' ? ` dans l'univers ${universeLabels[selectedUniverse as Universe]}` : ''}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-4">
          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
          ) : results.length > 0 ? (
            <div className="grid gap-3">
              {results.map((character) => (
                <button
                  key={character.id}
                  onClick={() => setSelectedCharacter(character)}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left group"
                >
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                    {character.profileImage ? (
                      <img src={character.profileImage} alt={character.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg font-bold text-gray-400">
                        {character.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900 truncate">{character.name}</h3>
                      <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                        {universeLabels[character.universe]}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">
                      {character.sections?.[0]?.content || 'Aucune description'}
                    </p>
                  </div>
                  <ChevronDown className="w-5 h-5 text-gray-400 -rotate-90 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          ) : searchTerm || selectedUniverse !== 'all' ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-3">
                <Search className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun personnage trouvé</h3>
              <p className="text-gray-500 text-sm">
                {searchTerm ? `Aucun résultat pour "${searchTerm}"` : 'Aucun personnage ne correspond aux filtres'}
              </p>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-3">
                <Search className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">Commencez votre recherche</h3>
              <p className="text-gray-500 text-sm">Utilisez la barre de recherche ou les filtres pour trouver des personnages</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
